import axios from 'axios'

// Auto-detect API URL: use api.suite.nz subdomain in production, otherwise use env var or localhost
const getBaseUrl = () => {
  // Check if running on suite.nz domain (production) - with or without www
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'suite.nz' || hostname === 'www.suite.nz') {
      return 'https://api.suite.nz' // Use subdomain for backend API
    }
  }
  // Use environment variable or fallback to localhost
  return import.meta.env.VITE_API_URL || 'http://localhost:3001'
}

const baseUrl = getBaseUrl()
const API_BASE_URL = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`

/** Per-device Simpro sync can take several minutes on large locations (DEPOT, vans). */
const SIMPRO_SYNC_TIMEOUT_MS = 300000

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Flag to prevent infinite refresh loops (shared between proactive and reactive refresh)
let isRefreshing = false
let failedQueue = []

// Helper function to check if token is expired or about to expire
const isTokenExpiringSoon = (token) => {
  if (!token) return true
  try {
    // Decode JWT without verification (we just need the expiry)
    const payload = JSON.parse(atob(token.split('.')[1]))
    const expiryTime = payload.exp * 1000 // Convert to milliseconds
    const now = Date.now()
    const timeUntilExpiry = expiryTime - now
    // Consider token expiring if less than 2 minutes remaining
    return timeUntilExpiry < 2 * 60 * 1000
  } catch (e) {
    // If we can't decode, assume it's expired
    return true
  }
}

function waitForTokenRefresh () {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject })
  })
}

// Helper function to refresh token proactively
const refreshTokenIfNeeded = async () => {
  const token = localStorage.getItem('token')
  const refreshToken = localStorage.getItem('refreshToken')

  if (!refreshToken) {
    return token
  }

  if (token && !isTokenExpiringSoon(token)) {
    return token
  }

  if (isRefreshing) {
    return waitForTokenRefresh()
  }

  isRefreshing = true

  try {
    const axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    })

    const response = await axiosInstance.post('/auth/refresh', { refreshToken })
    const { token: newAccessToken, accessToken, refreshToken: newRefreshToken } = response.data
    const tokenToUse = accessToken || newAccessToken

    if (!tokenToUse) {
      throw new Error('No token received from refresh endpoint')
    }

    localStorage.setItem('token', tokenToUse)

    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken)
    }

    window.dispatchEvent(
      new CustomEvent('tokenRefreshed', {
        detail: { token: tokenToUse },
      })
    )

    processQueue(null, tokenToUse)
    isRefreshing = false

    return tokenToUse
  } catch (error) {
    processQueue(error, null)
    isRefreshing = false

    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      window.dispatchEvent(new CustomEvent('tokenExpired'))
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return null
  }
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

// Request interceptor to add auth token and refresh if needed
api.interceptors.request.use(
  async (config) => {
    // Don't attach/refresh token for these auth routes
    const isPublicAuthRoute = config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh') ||
      config.url?.includes('/auth/forgot-password') || config.url?.includes('/auth/reset-password')
    
    if (!isPublicAuthRoute) {
      // Try to refresh token proactively if it's expiring soon
      const refreshedToken = await refreshTokenIfNeeded()
      const token = refreshedToken || localStorage.getItem('token')
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } else {
      // For public auth routes no token; for logout we need token
      const isLogout = config.url?.includes('/auth/logout')
      const token = localStorage.getItem('token')
      if (isLogout && token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)


// Response interceptor to handle auth errors and refresh tokens
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // Handle password change required
    if (error.response?.status === 403 && error.response?.data?.requiresPasswordChange) {
      // Update user state to force password change
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser)
          const updatedUser = { ...user, is_temporary_password: true }
          localStorage.setItem('user', JSON.stringify(updatedUser))
          // Trigger a custom event to notify useAuth hook
          window.dispatchEvent(new CustomEvent('passwordChangeRequired', { detail: updatedUser }))
        } catch (e) {
          // Ignore parse errors
        }
      }
      return Promise.reject(error)
    }
    
    // Handle 401 unauthorized or 403 with token error (token expired or invalid)
    // Check if it's a token error (not a permissions error)
    const isTokenError = error.response?.status === 401 || 
      (error.response?.status === 403 && 
       (error.response?.data?.error?.toLowerCase().includes('token') || 
        error.response?.data?.error?.toLowerCase().includes('expired') ||
        error.response?.data?.error?.toLowerCase().includes('invalid')))
    
    if (isTokenError && !originalRequest._retry) {
      // Don't try to refresh on login/refresh endpoints
      const isAuthRoute = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/forgot-password') || originalRequest.url?.includes('/auth/reset-password')
      
      if (!isAuthRoute) {
        originalRequest._retry = true
        
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          }).catch(err => {
            return Promise.reject(err)
          })
        }
        
        isRefreshing = true
        
        try {
          const refreshToken = localStorage.getItem('refreshToken')
          
          if (refreshToken) {
            // Try to refresh the access token
            // Use a new axios instance to avoid interceptors
            const axiosInstance = axios.create({
              baseURL: API_BASE_URL,
              timeout: 30000,
              headers: { 'Content-Type': 'application/json' },
            })
            const response = await axiosInstance.post('/auth/refresh', { refreshToken })
            
            const { token: newAccessToken, accessToken, refreshToken: newRefreshToken } = response.data
            const tokenToUse = accessToken || newAccessToken
            
            if (!tokenToUse) {
              throw new Error('No token received from refresh endpoint')
            }
            
            // Store new access token
            localStorage.setItem('token', tokenToUse)
            
            // Store new refresh token if provided (backend rotates refresh tokens for security)
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken)
            }
            
            // Dispatch event to notify SSE and other components that token was updated
            window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
              detail: { token: tokenToUse } 
            }))
            
            // Update authorization header
            originalRequest.headers.Authorization = `Bearer ${tokenToUse}`
            
            // Process queued requests
            processQueue(null, tokenToUse)
            isRefreshing = false
            
            // Retry original request
            return api(originalRequest)
          } else {
            // No refresh token, logout
            processQueue(new Error('No refresh token'), null)
            isRefreshing = false
            
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('user')
            // Trigger event to notify useAuth hook to logout
            window.dispatchEvent(new CustomEvent('tokenExpired'))
            // Redirect to login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login'
            }
            
            return Promise.reject(error)
          }
        } catch (refreshError) {
          // Refresh failed, logout
          processQueue(refreshError, null)
          isRefreshing = false
          
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          // Trigger event to notify useAuth hook to logout
          window.dispatchEvent(new CustomEvent('tokenExpired'))
          // Redirect to login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          
          return Promise.reject(refreshError)
        }
      }
    }
    
    return Promise.reject(error)
  }
)

/** Refresh access token if expired or expiring soon; use before firing parallel requests on page load. */
export async function ensureValidAccessToken () {
  return refreshTokenIfNeeded()
}

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  getProfile: () => api.get('/auth/profile'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword, mobileHandoff = false) =>
    api.post('/auth/reset-password', { token, newPassword, mobileHandoff }),
}

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  uploadProfilePhoto: async (id, formData) => {
    const response = await api.put(`/users/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    window.dispatchEvent(new CustomEvent('userProfilePhotoUpdated', { detail: { userId: id } }))
    return response
  },
  removeProfilePhoto: async (id) => {
    const response = await api.delete(`/users/${id}/photo`)
    window.dispatchEvent(new CustomEvent('userProfilePhotoUpdated', { detail: { userId: id } }))
    return response
  },
  fetchProfilePhoto: (id, version) => api.get(`/users/${id}/photo`, {
    params: version ? { v: version } : undefined,
    responseType: 'blob',
  }),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
}

// Items API
export const itemsAPI = {
  getAll: (params) => api.get('/items', { params }),
  getByCategory: (categoryId, params) => api.get('/items', { params: { category_id: categoryId, ...params } }),
  getById: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  getDepotStock: (params) => api.get('/items/depot-stock', { params }),
}

// Stock API
export const stockAPI = {
  getDashboardStats: () => api.get('/stock/dashboard-stats'),
  getOverview: () => api.get('/stock/overview'),
  getByLocation: (locationId) => api.get(`/stock/location/${locationId}`),
  getByItem: (itemId) => api.get(`/stock/item/${itemId}`),
  createMovement: (data) => api.post('/stock/movement', data),
  updateMovement: (id, data) => api.put(`/stock/movement/${id}`, data),
  deleteMovement: (id) => api.delete(`/stock/movement/${id}`),
  getMovements: (params) => api.get('/stock/movements', { params }),
  transferStock: (data) => api.post('/stock/transfer', data),
  transferStockSimpro: (data) => api.post('/stock/transfer-simpro', data),
  getSummary: () => api.get('/stock/summary'),
  importCSV: (locationId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/stock-import/import/${locationId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
}

// Locations API
export const locationsAPI = {
  getAll: () => api.get('/locations'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
}

// Suppliers API
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  syncFromSimpro: () => api.post('/suppliers/sync-from-simpro'),
}

// Clients API (Jobs module – admin only)
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  /** Returns client, contacts, sites, jobs (recent) */
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  createContact: (clientId, data) => api.post(`/clients/${clientId}/contacts`, data),
  updateContact: (clientId, contactId, data) =>
    api.put(`/clients/${clientId}/contacts/${contactId}`, data),
  deleteContact: (clientId, contactId) =>
    api.delete(`/clients/${clientId}/contacts/${contactId}`),
  createSite: (clientId, data) => api.post(`/clients/${clientId}/sites`, data),
  updateSite: (clientId, siteId, data) => api.put(`/clients/${clientId}/sites/${siteId}`, data),
  deleteSite: (clientId, siteId) => api.delete(`/clients/${clientId}/sites/${siteId}`),
  /** Admin only — body optional: maxCustomers (omit = sync all up to server cap), pageSize, skipArchived */
  syncFromSimpro: (body) => api.post('/clients/sync-from-simpro', body || {}),
}

// Quotations API (Jobs module – admin only)
export const quotationsAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  approve: (id) => api.post(`/quotations/${id}/approve`),
  delete: (id) => api.delete(`/quotations/${id}`),
}

// Geocode API (Nominatim – admin only, for job address → lat/lon)
export const geocodeAPI = {
  /** Single result for "Look up" */
  search: (query) => api.get('/geocode', { params: { q: query } }),
  /** Multiple results for address autocomplete */
  searchSuggestions: (query) => api.get('/geocode/search', { params: { q: query } }),
}

// Jobs API (Jobs module – admin only)
export const jobsAPI = {
  getDefectSheetSummary: (id) => api.get(`/jobs/${id}/defect-sheet-summary`),
  getAll: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  getActivity: (id) => api.get(`/jobs/${id}/activity`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
}

export const defectSheetAPI = {
  list: (params) => api.get('/defect-sheets', { params }),
  setArchived: (sheetId, archived) => api.patch(`/defect-sheets/${sheetId}/archive`, { archived }),
  create: () => api.post('/defect-sheets', {}),
  getById: (sheetId) => api.get(`/defect-sheets/${sheetId}`),
  saveById: (sheetId, data) => api.put(`/defect-sheets/${sheetId}`, data),
  setTaskDone: (sheetId, data) => api.patch(`/defect-sheets/${sheetId}/task-status`, data),
  deleteById: (sheetId) => api.delete(`/defect-sheets/${sheetId}`),
  sendById: (sheetId, payload = {}) => api.post(`/defect-sheets/${sheetId}/send`, payload),
  getEmailDraftById: (sheetId) => api.get(`/defect-sheets/${sheetId}/email-draft`),
  getPrintHtmlById: async (sheetId, content = 'all') => {
    const res = await api.get(`/defect-sheets/${sheetId}/print`, {
      params: { content },
      responseType: 'text',
    })
    return typeof res.data === 'string' ? res.data : String(res.data)
  },
  get: (jobId) => api.get(`/jobs/${jobId}/defect-sheet`),
  save: (jobId, data) => api.put(`/jobs/${jobId}/defect-sheet`, data),
  send: (jobId) => api.post(`/jobs/${jobId}/defect-sheet/send`, {}),
  getPrintHtml: async (jobId, content = 'all') => {
    const res = await api.get(`/jobs/${jobId}/defect-sheet/print`, {
      params: { content },
      responseType: 'text',
    })
    return typeof res.data === 'string' ? res.data : String(res.data)
  },
  downloadPdfById: (sheetId, content = 'all') =>
    api.get(`/defect-sheets/${sheetId}/pdf`, { params: { content }, responseType: 'blob' }),
  downloadPdf: (jobId, content = 'all') =>
    api.get(`/jobs/${jobId}/defect-sheet/pdf`, { params: { content }, responseType: 'blob' }),
  clientOptions: (search = '', clientId = null) =>
    api.get('/defect-sheets/client-options', {
      params: { search, ...(clientId ? { client_id: clientId } : {}) },
    }),
  collaboratorCandidates: (params) =>
    api.get('/defect-sheets/collaborator-candidates', { params }),
  uploadPhoto: (sheetId, formData) =>
    api.post(`/defect-sheets/${sheetId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updatePhotoDescription: (photoId, description) =>
    api.patch(`/defect-sheet-photos/${photoId}`, { description }),
  deletePhoto: (photoId) => api.delete(`/defect-sheet-photos/${photoId}`),
  fetchPhotoBlob: async (photoId, { signal } = {}) => {
    const res = await api.get(`/defect-sheet-photos/${photoId}/file`, { responseType: 'blob', signal })
    const data = res.data
    if (data instanceof Blob && data.type?.includes('json')) {
      const text = await data.text()
      let message = 'Failed to load photo'
      try {
        const parsed = JSON.parse(text)
        message = parsed.error || message
      } catch {
        // ignore parse errors
      }
      throw new Error(message)
    }
    if (!(data instanceof Blob)) {
      throw new Error('Invalid photo response')
    }
    return data
  },
}

// Worker jobs & time (role_id = 2)
export const workerJobsAPI = {
  getMine: (params) => api.get('/jobs/mine', { params }),
  getMineById: (id) => api.get(`/jobs/mine/${id}`),
  clockIn: (id, body) => api.post(`/jobs/${id}/clock-in`, body),
  clockOut: (id, body) => api.post(`/jobs/${id}/clock-out`, body),
}

export const travelAPI = {
  start: (body) => api.post('/travel/start', body),
  getCurrent: () => api.get('/travel/current'),
  list: (params) => api.get('/travel', { params }),
  listByJob: (jobId) => api.get('/travel', { params: { job_id: jobId } }),
  setManualDistance: (id, data) => api.patch(`/travel/${id}/distance`, data),
}

export const timeEntriesAPI = {
  getMySummary: (params) => api.get('/time-entries/me/summary', { params }),
  list: (params) => api.get('/time-entries', { params }),
  listByJob: (jobId) => api.get(`/time-entries/job/${jobId}`),
}

// Job stock usage (report – admin only)
export const jobStockUsageAPI = {
  getAll: (params) => api.get('/job-stock-usage', { params }),
}

// Movements API
export const movementsAPI = {
  getAll: (params) => api.get('/stock/movements', { params }),
  create: (data) => api.post('/stock/movement', data),
  update: (id, data) => api.put(`/stock/movement/${id}`, data),
  delete: (id) => api.delete(`/stock/movement/${id}`),
}

// Alerts API
export const alertsAPI = {
  getAll: (params) => api.get('/alerts', { params }),
  getAuto: () => api.get('/alerts/auto'),
  getLowStock: () => api.get('/alerts/low-stock'),
  create: (data) => api.post('/alerts', data),
  update: (id, data) => api.put(`/alerts/${id}`, data),
  delete: (id) => api.delete(`/alerts/${id}`),
  toggle: (id) => api.patch(`/alerts/${id}/toggle`),
  // Simpro integration
  syncFromSimpro: () => api.post('/alerts/simpro/sync'),
  updateSimproMinLevel: (data) => api.patch('/alerts/simpro/update-min-level', data),
  bulkUpdateSimpro: (data) => api.patch('/alerts/simpro/bulk-update', data),
}

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  getSubcategories: (categoryId) => api.get(`/categories/${categoryId}/subcategories`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  createSubcategory: (categoryId, data) => api.post(`/categories/${categoryId}/subcategories`, data),
  updateSubcategory: (categoryId, subcategoryId, data) => api.put(`/categories/${categoryId}/subcategories/${subcategoryId}`, data),
  deleteSubcategory: (categoryId, subcategoryId) => api.delete(`/categories/${categoryId}/subcategories/${subcategoryId}`),
}

// Material Requests API
export const materialRequestsAPI = {
  // Painters
  create: (data) => api.post('/material-requests', data),
  getMyRequests: () => api.get('/material-requests/my-requests'),
  getById: (id) => api.get(`/material-requests/${id}`),
  confirmPickup: (id) => api.post(`/material-requests/${id}/confirm-pickup`),
  
  // Admins
  getAll: (params) => api.get('/material-requests', { params }),
  getAdminById: (id) => api.get(`/material-requests/admin/${id}`),
  updateStatus: (id, data) => api.put(`/material-requests/${id}/status`, data),
}

export const restocksAPI = {
  getMyList: () => api.get('/restocks/my-list'),
  saveMyList: (data) => api.put('/restocks/my-list', data),
  getMyHistory: () => api.get('/restocks/my-history'),
  getPendingSummary: () => api.get('/restocks/pending-summary'),
  getPendingItems: () => api.get('/restocks/pending-items'),
  updatePendingItem: (id, data) => api.patch(`/restocks/pending-items/${id}`, data),
  removePendingItem: (id) => api.delete(`/restocks/pending-items/${id}`),
  createBatch: (data) => api.post('/restocks/batches', data),
  getCurrent: () => api.get('/restocks/current'),
  submitCurrent: (data) => api.put('/restocks/current/submission', data),
  getCycles: () => api.get('/restocks/cycles'),
  createCycle: (data) => api.post('/restocks/cycles', data),
  getSummary: (id) => api.get(`/restocks/cycles/${id}/summary`),
  downloadPdf: (id, type) => api.get(`/restocks/cycles/${id}/pdf`, { params: { type }, responseType: 'blob', timeout: 60000 }),
  updateStatus: (id, status) => api.patch(`/restocks/cycles/${id}/status`, { status }),
}

// Simpro API
export const simproAPI = {
  testConnection: () => api.get('/simpro/test-connection'),
  syncAll: () => api.post('/simpro/sync', {}, { timeout: SIMPRO_SYNC_TIMEOUT_MS }),
  syncDevices: (deviceIds) =>
    api.post('/simpro/sync/devices', { device_ids: deviceIds }, { timeout: SIMPRO_SYNC_TIMEOUT_MS }),
  startSyncChain: () => api.post('/simpro/sync/chain/start', {}),
  reconcileLocations: () => api.post('/simpro/sync/reconcile-locations', {}),
  pruneLocations: (payload = {}) => api.post('/simpro/sync/prune-locations', payload),
  syncDevice: (deviceId, { name } = {}) =>
    api.post(
      `/simpro/sync/device/${deviceId}`,
      name ? { name } : {},
      { timeout: SIMPRO_SYNC_TIMEOUT_MS }
    ),
  getStorageDevices: () => api.get('/simpro/storage-devices'),
  getUOMs: () => api.get('/simpro/uoms'),
}

// Weather API (proxy to Open-Meteo). Uses fetch so auth interceptors don't redirect on token issues.
export const weatherAPI = {
  getBaseUrl: () => API_BASE_URL,

  getForecast: async (location) => {
    const url = `${API_BASE_URL}/weather?location=${encodeURIComponent(location || 'Auckland')}`
    let res
    try {
      res = await fetch(url)
    } catch (networkErr) {
      const msg = networkErr?.message || 'Network error'
      const err = new Error(msg.includes('fetch') ? 'Cannot reach server. Is the backend running?' : msg)
      err.response = { status: 0, data: { message: err.message } }
      throw err
    }
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err = new Error(data.message || data.error || res.statusText || 'Weather unavailable')
      err.response = { status: res.status, data }
      throw err
    }
    // Backend may return 200 with empty/invalid shape (e.g. proxy error as HTML)
    if (data && typeof data.label === 'string' && Array.isArray(data.daily)) {
      return { data }
    }
    const err = new Error(data?.message || data?.error || 'Invalid weather response')
    err.response = { status: res.status, data }
    throw err
  },

  ping: async () => {
    const url = `${API_BASE_URL}/weather/ping`
    const res = await fetch(url)
    const data = await res.json().catch(() => ({}))
    return res.ok && data?.ok === true
  },
}

// Cron Sync API
export const cronSyncAPI = {
  getStatus: () => api.get('/cron-sync/status'),
}

// Permissions API
export const permissionsAPI = {
  getAll: () => api.get('/permissions'),
  getUserPermissions: (userId) => api.get(`/permissions/user/${userId}`),
  updateUserPermissions: (userId, permissionIds) => api.put(`/permissions/user/${userId}`, { permissionIds }),
}

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  updateStatus: (id, data) => api.patch(`/purchase-orders/${id}/status`, data),
  receiveItem: (poId, itemId, data) => api.post(`/purchase-orders/${poId}/items/${itemId}/receive`, data),
  removeItem: (poId, itemId) => api.delete(`/purchase-orders/${poId}/items/${itemId}`),
  updateCustomItemDetails: (poId, itemId, data) => api.patch(`/purchase-orders/${poId}/items/${itemId}/custom-details`, data),
  getSupplierItems: (supplierId, params) => api.get(`/purchase-orders/suppliers/${supplierId}/items`, { params }),
}

export default api
